import ym from 'react-yandex-metrika';

import { EXTERNAL_LMS_SLUGS, LESSON_STATUS, WIDGET_TYPE } from '@yandex-int/k-common/typings';
import { GRAVITIES, NOTIFICATION_THEMES } from 'platform-components/constants';
import { reportMessage } from 'platform-components/src/utils/raven-message';
import qs from 'query-string';

import { ASSIGNMENT_STATES, PROCESSABLE_ERRORS } from 'common.components/lesson-view.assignment/typings';
import { SourceAssignmentInfo } from 'common.components/lesson/typings';
import { isLessonInProgress } from 'common.components/lesson/utils';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import { redirectToMesHomeworkPageUrl } from 'utils/external-lms/external-lms';
import { getFullUrl } from 'utils/get-full-url';
import { getIsInTeacherShowcase } from 'utils/get-is-in-teacher-showcase';
import { getTeamIdFromPath } from 'utils/path';
import { queryCache } from 'react-query';
import { sendMessageFromIFrame } from 'utils/send-from-iframe';
import { queryKeys } from '../../../../../bundles/lab/components/library.course-container/queries';

import { ACTION_BUTTON_MODE } from '../../action-button-mode';

import { mixinContextMembers } from './context';
import { mixinSocketMembers } from './socket';
import { mixinToastMembers } from './toast';

export function mixinLessonAssignMembers<
    TBase extends ReturnType<typeof mixinContextMembers> &
        ReturnType<typeof mixinSocketMembers> &
        ReturnType<typeof mixinToastMembers>,
>(Base: TBase) {
    return class extends Base {
        addStudentsToExistingLesson = (clessonId: number) => {
            const { assignmentTable, selectedStudents, addStudentsToLesson } = this.props;

            // таблица assignmentTable содержит черный список учеников (то есть которым НЕ выдан урок)
            // поэтому нам нужны только те, кто есть в этом списке
            const addedStudents = selectedStudents.filter((student) => assignmentTable.includes(student.toString()));

            addStudentsToLesson({
                clessonId: clessonId,
                addedStudents,
                onSuccess: this.onAssignLesson,
            });

            ym('reachGoal', 'teacher_lesson_creation_additional_assignment');
        };

        getEvaluationDate = () => {
            const { lesson, isEvaluable, shouldGetAssignmentDatesFromMeta } = this.props;

            return isEvaluable && shouldGetAssignmentDatesFromMeta && lesson?.meta.evaluation_date
                ? new Date(lesson.meta.evaluation_date)
                : null;
        };

        handleAssignError = (errorMessage: PROCESSABLE_ERRORS) => {
            const { intl, setDeadlineDateError, showNetworkError, setAssignmentState } = this.props;

            const messages = {
                [PROCESSABLE_ERRORS.DEADLINE_BEFORE_ASSIGNMENT]: 'assignLessonModal.errors.incompatibleDateAssign',
                [PROCESSABLE_ERRORS.NOT_ENOUGH_TIME_TO_PREPARE]:
                    'assignLessonModal.errors.controlWork.incompatibleDateDeadline',
            };
            const message = messages[errorMessage];

            if (!message) {
                const errorText = intl.formatMessage({ id: 'assignLessonModal.errors.unknown' }, { errorMessage });
                showNetworkError(new Error(errorText));
                reportMessage(errorText);
            }

            setDeadlineDateError(intl.formatMessage({ id: message }));
            setAssignmentState(ASSIGNMENT_STATES.ERROR);
        };

        assignLesson = async () => {
            const {
                assignDate,
                deadlineDate,
                students: allStudents,
                selectedStudents,
                lesson,
                lessonMode,
                duration,
                assignLesson,
                intl,
                setWizardVariables,
                setAssignmentState,
                shouldShowSubscriptionModal,
                studentsTable,
                defaultCourseId,
                showIntlMessageNotification,
                startTeamBuilder,
                subject,
                mesBackUrl,
                isTeacherFromMes,
            } = this.props;

            if (!defaultCourseId) {
                setAssignmentState(ASSIGNMENT_STATES.IDLE);

                showIntlMessageNotification({
                    gravity: GRAVITIES.BOTTOM,
                    messageId: 'toast.lesson.subjectNotFound',
                    notificationAction: () => {
                        startTeamBuilder({
                            entryPoint: TEAM_BUILDER_ENTRY_STEPS.SUBJECTS,
                            newSubject: subject,
                        });
                    },
                    notificationMessageId: 'toast.lesson.subjectNotFound.addSubjectLink',
                    theme: NOTIFICATION_THEMES.TOAST,
                    mix: 'lesson-view__copy-link-toast',
                });
                return;
            }

            const isAllSelected = selectedStudents.length === allStudents?.length;
            const status = lesson.clesson?.status || LESSON_STATUS.NOT_ASSIGNED;
            let clessonId = lesson.clesson?.id;

            // если урок уже решают - докидываем учеников (ручка принимает список новых учеников)
            if (isLessonInProgress(status)) {
                this.addStudentsToExistingLesson(clessonId!);
            } else {
                // если урок еще не решают - выдаем урок
                const evaluationDate = this.getEvaluationDate();

                const assignmentInfo: SourceAssignmentInfo = {
                    assignmentTable: studentsTable,
                    assignmentDate: assignDate!,
                    deadlineDate: deadlineDate!,
                    lessonMode,
                    duration,
                    evaluationDate,
                };

                assignLesson({
                    assignmentInfo,
                    lessonId: clessonId!,
                    shouldShowSubscriptionModal,
                    onAssign: this.onAssignLesson,
                    onAssignError: this.handleAssignError,
                    onError: () => {
                        setAssignmentState(ASSIGNMENT_STATES.ERROR);
                    },

                    ...this.getAssignToastParams(),

                    mesBackUrl,
                    isTeacherFromMes,

                    intl,
                });

                setWizardVariables({ lessonEditorButtonAssignPressed: true });

                if (!isAllSelected) {
                    ym('reachGoal', 'teacher_lesson_creation_individual_assignment_success');
                }
            }
        };

        onAssignLesson = (assignedLessonId: number) => {
            const {
                buttonMode,
                mesBackUrl,
                getRouterPath,
                toggleLessonModal,
                location: { pathname },
                clearPrepLessonsDetails,
                lesson,
                courseId,
                currentClass,
                mainPageData,
                subject,
                isTeacherFromMes,
            } = this.props;
            const {
                staticUrls: { externalKids, externalShowAssignedLesson },
            } = this.context;

            this.emitSocket();

            if (buttonMode === ACTION_BUTTON_MODE.ASSIGN_IN_EXTERNAL_LMS && isTeacherFromMes) {
                if (typeof window !== 'undefined') {
                    const teamId = getTeamIdFromPath();
                    const studentLink = `${getFullUrl(externalKids)}?${qs.stringify({
                        course: courseId,
                        lesson: assignedLessonId,
                        class_id: teamId,
                        lms: EXTERNAL_LMS_SLUGS.MES,
                    })}`;
                    const teacherLink = `${getFullUrl(
                        getRouterPath(externalShowAssignedLesson, { classId: teamId })
                    )}?${qs.stringify({
                        lms: EXTERNAL_LMS_SLUGS.MES,
                        clesson_id: assignedLessonId,
                        subject,
                    })}`;

                    const materialName = lesson.name;

                    // isTeacherFromMes = true -> mesBackUrl not undefined
                    redirectToMesHomeworkPageUrl(mesBackUrl!, studentLink, teacherLink, materialName);
                }
            }

            if (getIsInTeacherShowcase(pathname)) {
                ym('reachGoal', 'teacher_main_week_assignment_lesson', {
                    lessonId: lesson.id,
                    showcaseWidgetSlug: WIDGET_TYPE.ACTUAL_CONTENT,
                    showcaseGrade: currentClass.number,
                    showcaseId: mainPageData?.id,
                });
            }

            // обновляем данные модулей после выдачи урока
            queryCache.invalidateQueries(queryKeys.getModuleLessons);
            toggleLessonModal(false);
            clearPrepLessonsDetails();

            sendMessageFromIFrame('COPY_CLESSON', {});
        };
    };
}
